<script setup>
import { computed, onActivated, onDeactivated, ref } from 'vue';
import WahaManagerAPI, { isWahaInbox } from 'dashboard/api/wahaManager';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import Avatar from 'next/avatar/Avatar.vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  useMapGetter,
  useStoreGetters,
  useStore,
} from 'dashboard/composables/store';
import ChannelName from './components/ChannelName.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();
const route = useRoute();
const router = useRouter();
const wahaState = ref({ sessions: [], cleanups: [] });
const pendingCleanups = computed(() =>
  wahaState.value.cleanups.filter(task => task.status !== 'DONE')
);
const wahaError = ref(false);
const deleting = ref(false);
let refreshTimer;
let active = false;
let refreshing = false;
const refreshWaha = async () => {
  if (!active || refreshing || String(route.params.accountId) !== '1') return;
  refreshing = true;
  try {
    const next = await WahaManagerAPI.state();
    if (!active) return;
    const completed = next.cleanups.some(
      task =>
        task.status === 'DONE' &&
        wahaState.value.cleanups.some(
          old => old.inbox_id === task.inbox_id && old.status !== 'DONE'
        )
    );
    wahaState.value = next;
    wahaError.value = false;
    if (completed) await store.dispatch('inboxes/get');
  } catch {
    if (active) wahaError.value = true;
  } finally {
    refreshing = false;
  }
};
const sessionFor = inbox =>
  wahaState.value.sessions.find(session => session.inbox_id === inbox.id);
const activeCleanup = inbox =>
  wahaState.value.cleanups.some(
    task => task.inbox_id === inbox.id && task.status !== 'DONE'
  );
const retryCleanup = async task => {
  if (deleting.value) return;
  deleting.value = true;
  try {
    await WahaManagerAPI.retryCleanup(task.inbox_id);
    await refreshWaha();
  } catch (error) {
    useAlert(error.response?.data?.error || t('BORDERCHAT.LOAD_ERROR'));
  } finally {
    deleting.value = false;
  }
};

const showDeletePopup = ref(false);
const selectedInbox = ref({});
const searchQuery = ref('');

const inboxes = useMapGetter('inboxes/getInboxes');

onActivated(() => {
  store.dispatch('inboxes/get');
  if (['#proxy-ip', '#proxies'].includes(route.hash)) {
    router.replace({ name: 'settings_waha_proxies', params: route.params });
    return;
  }
  active = true;
  refreshWaha();
  refreshTimer = setInterval(refreshWaha, 10000);
});
onDeactivated(() => {
  active = false;
  clearInterval(refreshTimer);
});

const inboxesList = computed(() => {
  return (inboxes.value || [])
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));
});

const filteredInboxesList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return inboxesList.value;
  return picoSearch(inboxesList.value, query, ['name', 'channel_type']);
});

const uiFlags = computed(() => getters['inboxes/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.YES')} ${selectedInbox.value.name}`
);

const deleteRejectText = computed(
  () => `${t('INBOX_MGMT.DELETE.CONFIRM.NO')} ${selectedInbox.value.name}`
);

const confirmDeleteMessage = computed(() =>
  isWahaInbox(selectedInbox.value)
    ? t('BORDERCHAT.DELETE_HINT')
    : `${t('INBOX_MGMT.DELETE.CONFIRM.MESSAGE')} ${selectedInbox.value.name}?`
);
const confirmPlaceHolderText = computed(
  () =>
    `${t('INBOX_MGMT.DELETE.CONFIRM.PLACE_HOLDER', {
      inboxName: selectedInbox.value.name,
    })}`
);

const deleteInbox = async inbox => {
  if (deleting.value) return;
  deleting.value = true;
  try {
    if (isWahaInbox(inbox)) {
      await WahaManagerAPI.cleanup(inbox.id);
      useAlert(t('BORDERCHAT.CLEANUP_ACCEPTED'));
      await refreshWaha();
    } else {
      await store.dispatch('inboxes/delete', inbox.id);
      useAlert(t('INBOX_MGMT.DELETE.API.SUCCESS_MESSAGE'));
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('INBOX_MGMT.DELETE.API.ERROR_MESSAGE')
    );
  } finally {
    deleting.value = false;
  }
};
const closeDelete = () => {
  showDeletePopup.value = false;
  selectedInbox.value = {};
};

const confirmDeletion = () => {
  deleteInbox(selectedInbox.value);
  closeDelete();
};
const openDelete = inbox => {
  showDeletePopup.value = true;
  selectedInbox.value = inbox;
};
</script>

<template>
  <SettingsLayout
    :no-records-found="
      !inboxesList.length && !pendingCleanups.length && !wahaError
    "
    :no-records-message="$t('INBOX_MGMT.LIST.404')"
    :is-loading="uiFlags.isFetching"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('INBOX_MGMT.HEADER')"
        :description="$t('INBOX_MGMT.DESCRIPTION')"
        :link-text="$t('INBOX_MGMT.LEARN_MORE')"
        :search-placeholder="$t('INBOX_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="inboxes"
      >
        <template v-if="inboxesList?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('INBOX_MGMT.COUNT', { n: inboxesList.length }) }}
          </span>
        </template>
        <template #actions>
          <router-link v-if="isAdmin" :to="{ name: 'settings_inbox_new' }">
            <Button :label="$t('SETTINGS.INBOXES.NEW_INBOX')" size="sm" />
          </router-link>
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <div
        v-if="wahaError"
        role="alert"
        class="mb-4 flex items-center gap-3 rounded-lg bg-n-alpha-2 p-3 text-sm text-n-slate-11"
      >
        <span>{{ $t('BORDERCHAT.LOAD_ERROR') }}</span>
        <Button :label="$t('BORDERCHAT.RETRY')" sm slate @click="refreshWaha" />
      </div>
      <div
        v-for="task in pendingCleanups"
        :key="task.inbox_id"
        class="mb-4 flex flex-wrap items-center gap-2 text-xs text-n-slate-11"
        role="status"
      >
        <span>{{
          $t('BORDERCHAT.CLEANUP_ITEM', {
            name: task.name,
            id: task.inbox_id,
          })
        }}</span>
        <span>{{ $t('BORDERCHAT.CLEANUP_STATUS.' + task.status) }}</span>
        <span v-if="task.status !== 'DONE'">{{
          $t('BORDERCHAT.CLEANUP_PHASE.' + task.phase)
        }}</span>
        <span v-if="task.error" class="text-n-ruby-11">{{ task.error }}</span>
        <Button
          v-if="task.status === 'NEEDS_REVIEW' && isAdmin"
          :label="$t('BORDERCHAT.RETRY')"
          sm
          slate
          :disabled="deleting"
          @click="retryCleanup(task)"
        />
      </div>
      <span
        v-if="!filteredInboxesList.length && searchQuery"
        class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
      >
        {{ $t('INBOX_MGMT.NO_RESULTS') }}
      </span>
      <div v-else class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="inbox in filteredInboxesList"
          :key="inbox.id"
          class="flex justify-between flex-row items-start gap-4 py-4"
        >
          <div class="flex items-center gap-4">
            <div
              v-if="inbox.avatar_url"
              class="bg-n-alpha-3 rounded-xl size-10 ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
            >
              <Avatar
                :src="inbox.avatar_url"
                :name="inbox.name"
                :size="24"
                rounded-full
              />
            </div>
            <div
              v-else
              class="size-10 justify-center bg-n-alpha-3 rounded-xl ring ring-n-solid-1 border border-n-strong shadow-sm grid place-items-center"
            >
              <ChannelIcon class="size-6 text-n-slate-10" :inbox="inbox" />
            </div>
            <div class="flex flex-col items-start gap-1">
              <span class="block text-heading-3 text-n-slate-12 capitalize">
                {{ inbox.name }}
              </span>
              <template v-if="isWahaInbox(inbox)">
                <span class="text-sm text-n-slate-11">{{
                  $t('BORDERCHAT.WAHA')
                }}</span>
                <span class="text-xs text-n-slate-10">{{
                  sessionFor(inbox)?.messaging_enabled && !wahaError
                    ? $t('BORDERCHAT.ENABLED')
                    : $t('BORDERCHAT.DISABLED')
                }}</span>
                <span
                  v-if="sessionFor(inbox)?.integration_error"
                  class="text-xs text-n-ruby-11"
                >
                  {{ sessionFor(inbox).integration_error }}
                </span>
              </template>
              <ChannelName
                v-else
                :channel-type="inbox.channel_type"
                :medium="inbox.medium"
                :voice-enabled="inbox.voice_enabled"
                class="text-body-main text-n-slate-11"
              />
            </div>
          </div>
          <div class="flex flex-wrap gap-3 justify-end">
            <router-link
              v-if="isAdmin && isWahaInbox(inbox)"
              :to="{ name: 'settings_waha' }"
            >
              <Button :label="$t('BORDERCHAT.MANAGE')" sm slate />
            </router-link>
            <router-link
              :to="{
                name: 'settings_inbox_show',
                params: { inboxId: inbox.id },
              }"
            >
              <Button
                v-if="isAdmin"
                v-tooltip.top="$t('INBOX_MGMT.SETTINGS')"
                icon="i-woot-settings"
                slate
                sm
              />
            </router-link>
            <Button
              v-if="isAdmin"
              v-tooltip.top="$t('INBOX_MGMT.DELETE.BUTTON_TEXT')"
              icon="i-woot-bin"
              slate
              sm
              class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
              :disabled="deleting || activeCleanup(inbox)"
              @click="openDelete(inbox)"
            />
          </div>
        </div>
      </div>
    </template>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="$t('INBOX_MGMT.DELETE.CONFIRM.TITLE')"
      :message="confirmDeleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedInbox.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </SettingsLayout>
</template>
